
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ChatCommand } from '../services/ai-chat.service';
import { BuyercaddyDataAgentService } from '../services/buyercaddy-data-agent.service';
import { ContactsBuyercaddyDataAgentService } from '../services/contacts-buyercaddy-data-agent.service';
import { GeminiAgentService } from '../services/gemini-agent.service';
import { INSTRUCTIONS } from '../services/instructions.constants';
import { GeminiSchemaType, ProxyAiResponseSchemaService } from '../services/proxy-ai-response-schema.service';

export interface GeminiEnrichmentOptions {
  aggressiveMode?: boolean;
}

export interface EnrichmentConfig {
  promptPrefix: string;
  systemInstruction: string;
  geminiInstruction?: string;
  fetchDataFn: (prompt: string, limit: number) => Promise<any>;
  geminiFn: (
    promptPrefix: string,
    systemInstruction: string,
    prompt: string,
    sessionId?: string,
    options?: GeminiEnrichmentOptions
  ) => Promise<any>;
}

@Injectable({
  providedIn: 'root'
})
export class ChatEnrichmentConfigService {
  constructor(
    private buyercaddyDataAgentService: BuyercaddyDataAgentService,
    private userProfileDataAgentService: ContactsBuyercaddyDataAgentService,
    private proxyAiResponseSchemaService: ProxyAiResponseSchemaService,
    private geminiAgentService: GeminiAgentService,
  ) {}

  public readonly config: Partial<Record<ChatCommand, EnrichmentConfig>> = {
    [ChatCommand.AnalyzeTechStacks]: {
      promptPrefix: 'Analyze tech stacks for company ',
      systemInstruction: INSTRUCTIONS.systemInstructionCompanySoftwareAnalysis,
      geminiInstruction: INSTRUCTIONS.systemInstructionCompanySoftwareAnalysisGemini,
      fetchDataFn: (prompt,limit) => firstValueFrom(this.buyercaddyDataAgentService.fetchData('Chat operating mode: '+ChatCommand.AnalyzeTechStacks+', User Promt: '+prompt, limit)),
      geminiFn: (promptPrefix, sys, prompt, _sessionId, _options) =>
        this.geminiAgentService
          .sendPrompt('Chat operating mode: '+promptPrefix + ', User Promt: ' + prompt, {
            context: sys,
            responseSchema: this.proxyAiResponseSchemaService.getResponseSchema(GeminiSchemaType.csvProducts),
            config: { temperature: 0.7 },
          })
          .then(res => res?.content),
    },
    [ChatCommand.FindCustomers]: {
      promptPrefix: 'Find customers of products ',
      systemInstruction: INSTRUCTIONS.systemInstructionCompaniesUseProduct,
      geminiInstruction: INSTRUCTIONS.systemInstructionCompaniesUseProductGemini,
      fetchDataFn: (prompt,limit) => firstValueFrom(this.buyercaddyDataAgentService.fetchData('Chat operating mode: '+ChatCommand.FindCustomers+', User Promt: '+prompt, limit)),
      geminiFn: (promptPrefix, sys, prompt, _sessionId, options) => {
        const responseSchema = this.proxyAiResponseSchemaService.getResponseSchema(GeminiSchemaType.csvCompanies);
        const isAggressive = options?.aggressiveMode ?? false;
        
        let instruction = sys;
        if (isAggressive) {
           const originalSourceDesc = `"Source": human-readable citation ("Company website", "Press release", "Gartner report 2024").`;
           const newSourceDesc = `"Source": For this field, you must extract the actual URI from the Grounding/Attribution metadata returned by the search tool. Do not simply copy text URLs. Start from any provided BuyerCaddy data; include it verbatim and label the source exactly as received. Must contain the exact clickable URI extracted ONLY from Google Search Grounding / Attribution metadata. Strict rules: Use ONLY links from Grounding metadata DO NOT copy URLs from page content DO NOT manually type or reconstruct URLs Prefer original redirect links when available: vertex.ai/... google.com/url?... If the search tool does not provide a clickable metadata link → DO NOT include the entry Forbidden: manually inserted links approximated URLs cleaned or shortened links STRICTLY REQUIRED: The exact clickable URI provided by the Google Search Grounding/Citation metadata. PRIORITY: Always prefer the raw 'vertex.ai' or 'google.com/url' redirect links found in the metadata if available, as they are verified. Do not manually type URLs found in text. If the search tool does not provide a clickable citation for a specific entry, do not include that entry`;
           
           instruction = instruction.replace(originalSourceDesc, newSourceDesc);
           instruction = `${instruction}\n${INSTRUCTIONS.aggressiveCustomersInstruction}`;
        }

        const config = { temperature: 0.7 };
        const model = isAggressive ? 'gemini-pro-latest' : undefined;
        const tools = isAggressive ? [{ googleSearch: {} }] : undefined;

        return this.geminiAgentService
          .sendPrompt('Chat operating mode: '+promptPrefix + ', User Promt: ' + prompt, {
            context: instruction,
            responseSchema,
            config,
            model,
            tools
          })
          .then(res => res?.content);
      },
    },
    [ChatCommand.FindEmployees]: {
      promptPrefix: 'Find profiles of employees',
      systemInstruction: INSTRUCTIONS.systemIstructionEmployee,
      fetchDataFn: (prompt,limit) => firstValueFrom(this.userProfileDataAgentService.fetchData('Chat operating mode: '+ChatCommand.FindEmployees+', User Promt: '+prompt, limit)),
      geminiFn: (promptPrefix, sys, prompt, _sessionId, _options) =>
        this.geminiAgentService
          .sendPrompt(promptPrefix + ' ' + prompt, {
            context: sys,
            responseSchema: this.proxyAiResponseSchemaService.getResponseSchema(GeminiSchemaType.Profiles),
            config: { temperature: 0.7 },
          })
          .then(res => res?.content),
    },
    [ChatCommand.ApiDocs]: {
      promptPrefix: 'API Documentation query',
      systemInstruction: INSTRUCTIONS.systemInstructionApiDocs,
      fetchDataFn: async () => ({ data: [] }),
      geminiFn: (promptPrefix, sys, prompt) =>
        this.geminiAgentService
          .sendPrompt(promptPrefix + ': ' + prompt, {
            context: sys,
            config: { temperature: 0.1 },
          })
          .then(res => res?.content),
    }
  };
}
